"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 1: MMC1 (SxROM)
 * ------------------------------------------------------------------ */
// MMC1 (SxROM / SUROM / SNROM / SOROM)

function register(Mappers) {
  Mappers[1] = function (nes) {
    this.nes = nes;
    this.reset();
  };

  Mappers[1].prototype = new Mappers[0]();

  Mappers[1].prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);

    // 5-bit serial shift register:
    this.regBuffer = 0;
    this.regBufferCounter = 0;

    // Control register ($8000-$9FFF). The power-on value here is PRG mode 1 with
    // 8 KB CHR pages and work RAM enabled, so that $C000-$FFFF holds the last bank
    // and the CPU finds its reset vector. Games that want one of the other modes
    // select it before doing anything else.
    this.control = 0x04;

    // Bits 2-3 of the control register select one of the four PRG banking modes.
    this.prgMode = 1;

    // Bit 3 selects the CHR bank size: 0 = one 8 KB page, 1 = two 4 KB pages.
    this.chrBankMode = 0;
    this.chrBank0 = 0;
    this.chrBank1 = 0;
    // Which CHR register was written last. On 512 KB boards (SUROM) bit 4 of
    // that register becomes the fifth PRG bank bit.
    this.lastChrReg = 1;
    this.prgBankSelect = 0;
    this.prgRamEnabled = true;

    // The 8 KB work RAM chip at $6000-$7FFF. It survives a soft reset, so it is
    // only allocated the first time.
    if (!this.prgRam) {
      this.prgRam = new Uint8Array(0x2000);
    }

    this.currentChrBank = 0;
    this.currentChrBank1 = 0;

    // Set when the ghost half of a read-modify-write pair reached the serial
    // port; the next write is the result half and must be swallowed. See
    // write().
    this.droppedRmwWrite = false;
  };

  Mappers[1].prototype.write = function (address, value) {
    // Writes to addresses other than MMC registers are handled by NROM.
    if (address < 0x8000) {
      if (address >= 0x6000 && address < 0x8000) {
        // The work RAM chip can be disabled from the PRG bank register's bit 4.
        if (!this.prgRamEnabled) {
          return;
        }
        this.prgRam[address - 0x6000] = value;
        if (typeof this.nes.opts.onBatteryRamWrite === "function") {
          this.nes.opts.onBatteryRamWrite(address, value);
        }
      }
      Mappers[0].prototype.write.apply(this, arguments);
      return;
    }

    // Read-modify-write instructions store twice: the byte that was in memory
    // first (the ghost write marked by the CPU), then the result one CPU cycle
    // later. The MMC1's serial port misses the second write when it comes that
    // close, so on real hardware only the original byte is shifted in. Ninja
    // Cop Saizou sends whole bank numbers that way, so the result write is
    // dropped here instead of being latched as an extra bit.
    if (this.droppedRmwWrite) {
      this.droppedRmwWrite = false;
      return;
    }
    if (this.nes.cpu.writeDummy) {
      this.droppedRmwWrite = true;
    }

    // The four registers are written through a 5-bit serial shift register:
    // bit 7 set means "reset", otherwise the low bit is shifted in.
    // Register addresses:
    //   $8000-$9FFF -> Reg 0 (Control)
    //   $A000-$BFFF -> Reg 1 (CHR Bank 0)
    //   $C000-$DFFF -> Reg 2 (CHR Bank 1)
    //   $E000-$FFFF -> Reg 3 (PRG Bank)
    if ((value & 0x80) !== 0) {
      // Reset buffering: writing with bit 7 set clears the shift register.
      this.regBuffer = 0;
      this.regBufferCounter = 0;

      // Clearing the shift register with bit 7 set also forces the upper bits
      // of the control register, whichever window was written: bits 2-3 become
      // PRG mode 3. The lower bits (mirroring and the lower PRG mode bit) keep
      // whatever they held. This used to be applied to $8000-$9FFF writes only,
      // but the reset is registered before the address is decoded, so games
      // that reset by INC-ing a ROM byte at $C000-$FFFF saw a different PRG
      // layout than they asked for.
      this.control |= 0x0c;
      this.updateControl();
      return;
    }

    // Shift in bit 0 of the value (LSB first).
    this.regBuffer |= (value & 1) << this.regBufferCounter;
    this.regBufferCounter++;

    if (this.regBufferCounter === 5) {
      // All 5 bits received; write to the target register.
      this.setReg(this.getRegNumber(address), this.regBuffer);

      // Reset buffer for next write:
      this.regBuffer = 0;
      this.regBufferCounter = 0;
    }
  };

  Mappers[1].prototype.setReg = function (reg, value) {
    switch (reg) {
      case 0:
        // Reg 0: Control register ($8000-$9FFF)
        this.control = value;
        this.updateControl();
        this.updatePrgBank();
        break;

      case 1:
        // Reg 1: CHR Bank 0 ($A000-$BFFF)
        // In 8 KB CHR mode: bits 0-3 select 8 KB page (bit 4 is 9th bank bit)
        // In 4 KB CHR mode: bits 0-4 select 4 KB page for $0000-$0FFF
        this.chrBank0 = value;
        this.lastChrReg = 1;
        this.updateChrBanks();
        // On SUROM the fifth PRG bit lives in this register, so the PRG side
        // has to be re-evaluated too (Mesen does the same on every write).
        this.updatePrgBank();
        break;

      case 2:
        // Reg 2: CHR Bank 1 ($C000-$DFFF)
        // Only used in 4 KB CHR mode: selects 4 KB page for $1000-$1FFF
        this.chrBank1 = value;
        this.lastChrReg = 2;
        this.updateChrBanks();
        this.updatePrgBank();
        break;

      case 3:
        // Reg 3: PRG Bank ($E000-$FFFF)
        // Lower 5 bits select which 16 KB or 32 KB bank maps to the switchable window.
        this.prgBankSelect = value;
        this.updatePrgBank();
        break;
    }
  };

  Mappers[1].prototype.updateControl = function () {
    var control = this.control;

    // Bits 0-1: Mirroring (0=one-screen lower, 1=one-screen upper, 2=vertical, 3=horizontal)
    // Note: MMC1 mirroring bits are the same as iNES header convention.
    var mirroring = control & 3;
    switch (mirroring) {
      case 0:
        this.setMirroring(this.nes.rom.SINGLESCREEN_MIRRORING);
        break;
      case 1:
        this.setMirroring(this.nes.rom.SINGLESCREEN_MIRRORING2);
        break;
      case 2:
        this.setMirroring(this.nes.rom.VERTICAL_MIRRORING);
        break;
      case 3:
        this.setMirroring(this.nes.rom.HORIZONTAL_MIRRORING);
        break;
    }

    // Bits 2-3 select the PRG banking mode:
    //   0: One 32 KB bank at $8000 (mode 0)
    //   1: One 16 KB bank at $8000, last 16 KB fixed at $C000 (mode 1)
    //   2: Second-to-last 16 KB bank at $8000, one 16 KB bank at $C000 (mode 2)
    //   3: Work RAM at $6000 + mode 1 layout (mode 3, same as mode 1 + WRAM at $6000)
    // Formula: mode = (bit3 * 2) + bit2
    this.prgMode = (control >> 2) & 3;

    // Bit 4 selects the CHR bank size: 0 = one 8 KB page, 1 = two 4 KB pages.
    // This used to read bit 3, which is the high bit of the PRG mode. A game that
    // asked for 4 KB CHR pages while running the usual 16 KB PRG layout therefore
    // got the two pattern tables stacked from a single 8 KB page, and its tiles
    // came out scrambled (Pinball Quest was one).
    this.chrBankMode = (control >> 4) & 1;

    this.updatePrgBank();
    this.updateChrBanks();
  };

  Mappers[1].prototype.updateChrBanks = function () {
    var rom = this.nes.rom;
    if (rom.vromCount === 0) {
      return;
    }
    var numBanks = rom.vromCount;
    if (this.chrBankMode === 0) {
      // 8 KB CHR mode: register 0 selects one 8 KB page for both pattern tables.
      // Its low bit is not connected to the CHR address lines, so it is masked
      // off; the result is the 4 KB page the 8 KB window starts on.
      this.load8kVromBank(this.chrBank0 & 0x1e, 0x0000);
      return;
    }
    // 4 KB CHR mode: two independent 4 KB pages, one per pattern table.
    // Each register is 5 bits (0-31), directly selecting a 4 KB page (0-31).
    // Use modulo for wrapping (handles CHR ROM < 128 KB).
    var index0 = this.chrBank0 % numBanks;
    var index1 = this.chrBank1 % numBanks;
    this.currentChrBank = index0;
    this.currentChrBank1 = index1;
    this.loadVromBank(index0, 0x0000);
    this.loadVromBank(index1, 0x1000);
  };

  Mappers[1].prototype.updatePrgBank = function () {
    var romCount = this.nes.rom.romCount;

    // The MMC1B disables the work RAM chip from bit 4 of the PRG bank register
    // (0 = enabled, 1 = open bus). It used to be gated on control bit 4, which is
    // actually the CHR bank size: a game in 4 KB CHR mode silently lost its work
    // RAM. The 512 KB SUROM/SXROM boards reuse this bit as a PRG bank bit, so it
    // is only treated as a gate for ROMs small enough not to need it.
    this.prgRamEnabled = romCount > 16 || (this.prgBankSelect & 0x10) === 0;

    // The PRG register keeps four bits. On a 512 KB board (SUROM) the fifth
    // bank bit (A18) is bit 4 of whichever CHR register was written last, and
    // Dragon Warrior III/IV bank their 512 KB through it.
    var select = this.prgBankSelect & 0x0f;
    if (romCount > 16) {
      var chr =
        this.chrBankMode === 1 && this.lastChrReg === 2
          ? this.chrBank1
          : this.chrBank0;
      select |= chr & 0x10;
    }
    var bank = select % romCount;

    // PRG-RAM (WRAM) at $6000-$7FFF is mapped whenever enabled, regardless of the
    // PRG mode, so games that keep their data there in mode 3 keep working.
    if (this.prgRamEnabled) {
      this.loadWorkRam(0x6000);
    } else {
      // Clear the window when disabled
      for (var i = 0; i < 0x2000; i++) {
        this.nes.cpu.mem[0x6000 + i] = 0;
      }
    }

    switch (this.prgMode) {
      case 0: {
        // Mode 0: one 32 KB window fills $8000-$FFFF. The register holds a
        // 16 KB page number here (A14 is its low bit, which is ignored), so the
        // window starts at the even page it points to: register 2 maps 16 KB
        // banks 2 and 3, not 4 and 5. Feeding the value to load32kRomBank(),
        // which counts 32 KB banks, put every game in this mode one bank too
        // high; Ninja Cop Saizou jumped into an empty bank the first time it
        // switched away from bank 0.
        var page = (select & 0x1e) % romCount;
        this.loadRomBank(page, 0x8000);
        this.loadRomBank((page + 1) % romCount, 0xc000);
        break;
      }

      case 1:
        // Mode 1: One 16 KB bank at $8000, the last bank fixed at $C000.
        // Standard configuration for most MMC1 games.
        this.loadRomBank(bank, 0x8000);
        this.loadRomBank(romCount - 1, 0xc000);
        break;

      case 2:
        // Mode 2: First 16 KB bank fixed at $8000, one 16 KB bank switchable at $C000.
        // Used by some games to keep a fixed kernel in the first bank.
        this.loadRomBank(0, 0x8000);
        this.loadRomBank(bank, 0xc000);
        break;

      default:
        // Mode 3: Work RAM chip at $6000, plus mode 1 layout.
        // The work RAM is mapped at $6000-$7FFF, and PRG ROM uses mode 1 layout.
        // This is the mode CHR-RAM games use to keep their code in RAM.
        this.loadRomBank(bank, 0x8000);
        this.loadRomBank(romCount - 1, 0xc000);
        break;
    }
  };

  // Copies the 8 KB work RAM into a CPU memory window. The CPU reads that window
  // from CPU memory, so both have to be kept in step.
  Mappers[1].prototype.loadWorkRam = function (address) {
    for (var i = 0; i < 0x2000; i++) {
      this.nes.cpu.mem[address + i] = this.prgRam[i];
    }
  };

  // Returns the register number from the address written to:
  // $8000-$9FFF -> Reg 0 (Control)
  // $A000-$BFFF -> Reg 1 (CHR Bank 0)
  // $C000-$DFFF -> Reg 2 (CHR Bank 1)
  // $E000-$FFFF -> Reg 3 (PRG Bank)

  Mappers[1].prototype.getRegNumber = function (address) {
    if (address >= 0x8000 && address <= 0x9fff) {
      return 0;
    } else if (address >= 0xa000 && address <= 0xbfff) {
      return 1;
    } else if (address >= 0xc000 && address <= 0xdfff) {
      return 2;
    } else {
      return 3;
    }
  };

  Mappers[1].prototype.loadROM = function () {
    if (!this.nes.rom.valid) {
      throw new Error("MMC1: Invalid ROM! Unable to load.");
    }

    this.reset();
    // Power on clears the work RAM chip.
    this.prgRam = new Uint8Array(0x2000);
    this.loadCHRROM();
    this.loadBatteryRam();
    this.loadTrainer();
    this.updateControl();
    this.resetIrqHandler();
  };

  Mappers[1].prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    // A number, not a buffer: the serial shift register accumulates the five bits of
    // a value into this one integer, so there is nothing to copy.
    s.regBuffer = this.regBuffer;
    s.regBufferCounter = this.regBufferCounter;
    s.control = this.control;
    s.chrBank0 = this.chrBank0;
    s.chrBank1 = this.chrBank1;
    s.prgBankSelect = this.prgBankSelect;
    s.lastChrReg = this.lastChrReg;
    s.prgMode = this.prgMode;
    s.chrBankMode = this.chrBankMode;
    s.droppedRmwWrite = this.droppedRmwWrite;
    s.prgRam = Array.prototype.slice.call(this.prgRam);
    return s;
  };

  Mappers[1].prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);
    this.regBuffer = s.regBuffer;
    this.regBufferCounter = s.regBufferCounter;
    this.control = s.control;
    this.chrBank0 = s.chrBank0;
    this.chrBank1 = s.chrBank1;
    this.prgBankSelect = s.prgBankSelect;
    this.lastChrReg = s.lastChrReg === 2 ? 2 : 1;
    this.prgMode = s.prgMode;
    this.chrBankMode = s.chrBankMode;
    this.droppedRmwWrite = s.droppedRmwWrite === true;
    if (s.prgRam && this.prgRam) {
      for (var i = 0; i < this.prgRam.length; i++) {
        this.prgRam[i] = s.prgRam[i];
      }
    }
    this.updateControl();
  };
}

module.exports = { register: register };
